"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { getUserSubscriptions, getPlans, createSubscription } from "../utils/api"
import {
  BadgeCheck,
  Crown,
  Sparkles,
  ArrowUp,
  X,
  Star,
  Zap,
  Calendar,
  CreditCard,
  Shield,
  Rocket,
  Gift,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Settings,
  Diamond,
  Flame,
  Menu,
} from "lucide-react"
import toast from "react-hot-toast"
import ModernHeader from "./modern-header"
import ModernSidebar from "./admin_sidebar"
import { ClipLoader } from "react-spinners"
import { useQuery, useMutation, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { initializePayment } from "../utils/paymentUtils";
import { PaymentType } from '../utils/paymentTypes';
import StartupProfileModal from "../components/StartupProfileModal";
import { isGuest } from "../utils/isGuest"

// Create a client
const queryClient = new QueryClient()

const AdminSettingWrapper = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AdminSetting />
    </QueryClientProvider>
  )
}

const AdminSetting = () => {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isClosingDrawer, setIsClosingDrawer] = useState(false)
  const [processingPlanId, setProcessingPlanId] = useState(null)
  const [email, setEmail] = useState("")
  const [fullName, setFullName] = useState("")
  const [isMobile, setIsMobile] = useState(false)
  const [showProfileCompletionModal, setShowProfileCompletionModal] = useState(false);
  const [isRegisteredStartup, setIsRegisteredStartup] = useState(false);
  const isGuestValid=isGuest()


  // Check for mobile on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 1024
      setIsMobile(mobile)
      // On desktop, always show sidebar
      if (!mobile) setSidebarOpen(true)
    }

    checkIfMobile()
    window.addEventListener('resize', checkIfMobile)
    return () => window.removeEventListener('resize', checkIfMobile)
  }, [])

  useEffect(() => {
    // Check if we should show the profile completion modal
    const shouldShowModal = localStorage.getItem('showProfileCompletionModal') === 'true';
    if (shouldShowModal) {
      setShowProfileCompletionModal(true);
      localStorage.removeItem('showProfileCompletionModal');
    }
  }, []);

  useEffect(() => {
    const startupName = localStorage.getItem("startupName");
    const startupId = localStorage.getItem("startupId");
    
    if (startupId && !startupName) {
      setShowProfileCompletionModal(true);
    }
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev)
  }

  // Fetch subscription data with TanStack Query
  const { data: subscription, isLoading: subscriptionLoading, refetch: refetchSubscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const data = await getUserSubscriptions()
      return data?.[0] || null
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch profile data
  const { isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const startupId = localStorage.getItem("startupId")
      const response = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/api/startups/${startupId}`)
      setFullName(response.data.fullName)
      setEmail(response.data.email)
      return response.data
    },
  })

  // Fetch plans data (only when modal is opened)
  const { data: availablePlans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const all = await getPlans()
      return all
        .filter((p) => p.type === "Membership" && p.title !== (subscription?.title || ""))
        .map((p) => ({
          id: p._id,
          type: p.type,
          name: p.title,
          price: p.price,
          features: p.features && p.features.length > 0 ? p.features : ["No features listed"],
          tenure: p.tenure,
        }))
    },
    enabled: showUpgradeModal, // Only fetch when modal is open
    staleTime: 1000 * 60 * 10, // 10 minutes
  })

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: async ({ plan, token }) => {
      return createSubscription(
        { membership_id: plan.id, type: plan.type, payment_status: "Paid", orderId: plan.orderId },
        token
      )
    },
    onSuccess: () => {
      toast.success("Plan upgraded successfully!")
      refetchSubscription()
      closeUpgradeDrawer()
    },
    onError: (err) => {
      console.error(err)
      toast.error(err.message || "Upgrade failed")
    }
  })

  const isSubscriptionExpired = (sub) => {
    if (!sub) return true
    const startDate = new Date(sub.createdAt)
    const expiryDate = new Date(startDate)
    expiryDate.setDate(startDate.getDate() + Number(sub.tenure || 0))
    return new Date() > expiryDate
  }

  const getRemainingDays = (sub) => {
    if (!sub || !sub.tenure) return 0
    const startDate = new Date(sub.createdAt)
    const expiryDate = new Date(startDate)
    expiryDate.setDate(startDate.getDate() + Number(sub.tenure))
    const timeDiff = expiryDate - new Date()
    const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
    return daysLeft > 0 ? daysLeft : 0
  }

  const updatePlan = () => {
    if(isGuestValid){
      toast.error("Please login to subscribe on plans!")
      navigate("/login")
      return
    }
    navigate("/#pricing-section")
  }

  const openUpgradeModal = async () => {
    setShowUpgradeModal(true)
  }

  const expired = subscription ? isSubscriptionExpired(subscription) : true

  const closeUpgradeDrawer = () => {
    setIsClosingDrawer(true)
    setTimeout(() => {
      setShowUpgradeModal(false)
      setIsClosingDrawer(false)
    }, 400)
  }

  const getStatusColor = (daysRemaining) => {
    if (daysRemaining > 30) return "text-green-600 bg-green-100"
    if (daysRemaining > 7) return "text-yellow-600 bg-yellow-100"
    return "text-red-600 bg-red-100"
  }

  const handleChoosePlan = async (plan) => {
    setProcessingPlanId(plan.id);
    try {
      const token = localStorage.getItem("Startuptoken");
      if (!token) {
        toast.error("Please log in to continue");
        setProcessingPlanId(null);
        return;
      }

      // Store plan data in sessionStorage for verification
      sessionStorage.setItem(
        "subscription_plan",
        JSON.stringify({
          id: plan.id,
          name: plan.name,
          price: plan.price,
          tenure: plan.tenure,
          type: plan.type,
          features: plan.features
        })
      );

      // Initialize payment
      const paymentResult = await initializePayment({
        type: PaymentType.SUBSCRIPTION,
        amount: plan.price,
        token,
        returnUrl: `${window.location.origin}/payment-callback`
      });

      // Show loading state
      toast.loading("Processing payment...", { id: "payment" });

    } catch (error) {
      console.error("Payment Error:", error);
      toast.error(error.message || "Payment processing failed");
      setProcessingPlanId(null);
    }
  };

  // Skeleton Loader Components
  const SubscriptionSkeleton = () => (
    <div className="bg-white/80 backdrop-blur-sm border border-white/20 rounded-3xl p-8 shadow-lg">
      <div className="flex items-center justify-center space-x-3">
        <ClipLoader />
        <p className="text-gray-600 font-medium">Loading subscription details...</p>
      </div>
    </div>
  )

  const PlanCardSkeleton = () => (
    <div className="border border-gray-200 rounded-2xl p-6 animate-pulse">
      <div className="flex justify-between items-center mb-4">
        <div className="h-6 bg-gray-200 rounded w-32"></div>
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
      <div className="space-y-2 mb-4">
        <div className="h-3 bg-gray-200 rounded w-full"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
    </div>
  )

  const ProfileInfoSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-64"></div>
    </div>
  )

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden relative">
      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:relative z-40 h-full
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <ModernSidebar
          isOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
          fullName={fullName}
          email={email}
        />
      </div>

      <div className={`flex flex-col flex-1 transition-all duration-300 ${isMobile ? "ml-0" : "ml-16 sm:ml-18 md:ml-20 lg:ml-20 xl:ml-20"}`}>
        <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="
          px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              {profileLoading ? (
                <ProfileInfoSkeleton />
              ) : (
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-black via-gray-900 to-slate-600 bg-clip-text text-transparent">
                    Account Settings
                  </h1>
                  <p className="text-gray-600 mt-2">Manage your subscription and account preferences</p>
                </div>
              )}
            </div>

            {/* Content Sections */}
            <div className="mt-8">
              {subscriptionLoading ? (
                <SubscriptionSkeleton />
              ) : expired ? (
                <div className="space-y-6">
                  {/* Expired/No Subscription Card */}
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-3xl p-6 sm:p-8 shadow-lg">
                    <div className="text-center">
                      <div className="bg-red-100 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                        <AlertTriangle className="w-10 h-10 text-red-600" />
                      </div>
                      {subscription ? (
                        <>
                          <h2 className="text-2xl font-bold text-red-700 mb-3">Subscription Expired</h2>
                          <p className="text-red-600 mb-6">
                            Your {subscription.title} plan has expired. Renew now to continue accessing premium features.
                          </p>
                        </>
                      ) : (
                        <>
                          <h2 className="text-2xl font-bold text-orange-700 mb-3">No Active Subscription</h2>
                          <p className="text-orange-600 mb-6">
                            Subscribe to unlock all premium features and grow your startup faster.
                          </p>
                        </>
                      )}
                      <button
                        onClick={updatePlan}
                        className="group relative overflow-hidden bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3 mx-auto"
                      >
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                        <Rocket className="w-5 h-5" />
                        <span>{subscription ? "Renew Subscription" : "Subscribe Now"}</span>
                      </button>
                    </div>
                  </div>

                  {/* Benefits Preview */}
                  <div className="bg-white/80 backdrop-blur-sm border border-white/20 rounded-3xl p-6 sm:p-8 shadow-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">What You're Missing Out On</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <Shield className="w-8 h-8 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Premium Security</h4>
                        <p className="text-gray-600 text-sm">Advanced security features and data protection</p>
                      </div>
                      <div className="text-center">
                        <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <TrendingUp className="w-8 h-8 text-purple-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Analytics Dashboard</h4>
                        <p className="text-gray-600 text-sm">Detailed insights and performance metrics</p>
                      </div>
                      <div className="text-center">
                        <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <Zap className="w-8 h-8 text-green-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Priority Support</h4>
                        <p className="text-gray-600 text-sm">24/7 dedicated customer support</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Active Subscription Card */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-3xl p-6 sm:p-8 shadow-lg">
                    <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-3 rounded-full">
                          <BadgeCheck className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-green-800">Active Subscription</h2>
                          <p className="text-green-600">Your plan is active and running smoothly</p>
                        </div>
                      </div>
                      <div className="sm:text-right">
                        <div
                          className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(getRemainingDays(subscription))}`}
                        >
                          {getRemainingDays(subscription)} days left
                        </div>
                      </div>
                    </div>

                    {/* Subscription Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <Crown className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm font-medium">Plan Name</p>
                            <p className="text-lg font-bold text-blue-600">{subscription.title}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-green-100 p-2 rounded-lg">
                            <CreditCard className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm font-medium">Price Paid</p>
                            <p className="text-lg font-bold text-green-600">₹{subscription.price}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-purple-100 p-2 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm font-medium">Payment Status</p>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <p className="text-sm font-bold text-green-600">PAID</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <Calendar className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm font-medium">Subscribed On</p>
                            <p className="text-sm font-bold text-orange-600">
                              {new Date(subscription.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-indigo-100 p-2 rounded-lg">
                            <Clock className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm font-medium">Plan Duration</p>
                            <p className="text-sm font-bold text-indigo-600">
                              {subscription.tenure
                                ? `${Math.round(subscription.tenure / 30)} month${Math.round(subscription.tenure / 30) > 1 ? "s" : ""}`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-red-100 p-2 rounded-lg">
                            <TrendingUp className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="text-gray-500 text-sm font-medium">Days Remaining</p>
                            <p className="text-sm font-bold text-red-600">{getRemainingDays(subscription)} days</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Features Section */}
                    <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-500" />
                        Features Included
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {subscription.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Upgrade Section */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-3xl p-6 sm:p-8 shadow-lg">
                    <div className="text-center">
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-full">
                          <Crown className="w-8 h-8 text-white animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Ready for More?</h3>
                          <p className="text-gray-600">Unlock additional features and benefits</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="text-center">
                          <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <Diamond className="w-8 h-8 text-purple-600" />
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">Premium Features</h4>
                          <p className="text-gray-600 text-sm">Access to exclusive premium tools</p>
                        </div>
                        <div className="text-center">
                          <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <Flame className="w-8 h-8 text-purple-600" />
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">Enhanced Performance</h4>
                          <p className="text-gray-600 text-sm">Faster processing and better reliability</p>
                        </div>
                        <div className="text-center">
                          <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <Gift className="w-8 h-8 text-purple-600" />
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">Exclusive Benefits</h4>
                          <p className="text-gray-600 text-sm">Special perks and early access</p>
                        </div>
                      </div>

                      <button
                        onClick={openUpgradeModal}
                        className="group relative overflow-hidden bg-black hover:from-purple-700 hover:via-pink-700 hover:to-indigo-700 text-white px-10 py-4 rounded-xl font-bold shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-700 via-pink-700 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative flex items-center gap-3">
                          <Crown className="w-6 h-6 group-hover:animate-bounce" />
                          <span className="bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
                            Upgrade Plan
                          </span>
                          <ArrowUp className="w-5 h-5 group-hover:animate-bounce group-hover:translate-y-[-2px] transition-transform duration-300" />
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-xl"></div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Upgrade Drawer */}
      {showUpgradeModal && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${isClosingDrawer ? "animate-fadeOut" : "animate-fadeIn"
              }`}
            onClick={closeUpgradeDrawer}
          />
          {/* Drawer */}
          <div
            className={`fixed inset-y-0 right-0 z-50 w-full sm:w-96 md:w-[500px] lg:w-[600px] xl:w-[700px] bg-white shadow-2xl transform transition-transform duration-400 ease-in-out ${isClosingDrawer ? "animate-slideOutRight" : "animate-slideInRight"
              }`}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 p-6 text-white">
              <button
                onClick={closeUpgradeDrawer}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="pr-12">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Crown className="w-8 h-8 text-yellow-300" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Upgrade Your Plan</h3>
                    <p className="text-purple-100">Unlock premium features and benefits</p>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-sm text-purple-100">
                    🚀 Choose from our premium plans to supercharge your startup journey
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="h-full overflow-y-auto pb-24">
              <div className="p-6">
                <div className="mb-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Available Plans
                  </h4>
                  <p className="text-gray-600 text-sm">Choose the perfect plan for your needs</p>
                </div>

                {plansLoading ? (
                  <div className="space-y-6">
                    <PlanCardSkeleton />
                    <PlanCardSkeleton />
                  </div>
                ) : availablePlans.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Crown className="w-10 h-10 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Upgrades Available</h3>
                    <p className="text-gray-500 mb-2">You're already on the best plan available!</p>
                    <p className="text-gray-400 text-sm">Check back later for new premium options.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {availablePlans.map((plan, index) => (
                      <div
                        key={plan.id}
                        className={`relative border-2 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl ${index === 0
                          ? "border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg"
                          : "border-gray-200 hover:border-purple-200 bg-white"
                          }`}
                      >
                        {index === 0 && (
                          <div className="absolute -top-3 left-6">
                            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              MOST POPULAR
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row justify-between items-start mb-4 pt-2 gap-4">
                          <div>
                            <h5 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                              {plan.name}
                              {index === 0 && <Crown className="w-5 h-5 text-purple-600" />}
                            </h5>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-4 h-4 text-gray-500" />
                              <p className="text-gray-600 text-sm">
                                {plan.tenure
                                  ? `${Math.round(plan.tenure / 30)} month${Math.round(plan.tenure / 30) > 1 ? "s" : ""}`
                                  : "N/A"}{" "}
                                duration
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-3xl font-bold text-gray-900">₹{plan.price}</div>
                            <div className="text-gray-500 text-sm">one-time payment</div>
                          </div>
                        </div>

                        <div className="mb-6">
                          <h6 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                            <Zap className="w-4 h-4 text-yellow-500" />
                            What's Included:
                          </h6>
                          <div className="space-y-2">
                            {plan.features.slice(0, 4).map((feature, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full"></div>
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </div>
                            ))}
                            {plan.features.length > 4 && (
                              <div className="text-sm text-purple-600 ml-5 font-medium">
                                +{plan.features.length - 4} more premium features
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() =>
                            !processingPlanId && plan.name !== subscription?.title && handleChoosePlan(plan)
                          }
                          disabled={processingPlanId === plan.id || plan.name === subscription?.title}
                          className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 text-sm ${index === 0
                            ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-pink-700"
                            : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                            } ${(processingPlanId === plan.id || plan.name === subscription?.title) &&
                            "opacity-50 cursor-not-allowed"
                            }`}
                        >
                          {plan.name === subscription?.title ? (
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Current Plan
                            </div>
                          ) : processingPlanId === plan.id ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Processing...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <Rocket className="w-4 h-4" />
                              Choose This Plan
                            </div>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-6">
              <div className="flex gap-4">
                <button
                  onClick={closeUpgradeDrawer}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium"
                  disabled={createSubscriptionMutation.isPending}
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showProfileCompletionModal && (
        <StartupProfileModal
          startupId={localStorage.getItem("startupId")}
          onComplete={() => {
            setShowProfileCompletionModal(false);
          }}
        />
      )}
    </div>
  )
}

export default AdminSettingWrapper